
import { get } from 'lodash-es'
import { projectCookieData } from '@/config/functions'
import { EDateType, EProjectStatus } from '@/helpers/enums'
import { projectDetails } from '@/helpers/project'

export default {
  name: 'ShowProject',

  props: {
    projectData: {
      type: Object,
      required: true
    },
    applicationData: {
      type: Object,
      default: () => {},
      required: false
    }
  },

  data() {
    return {
      EDateType,
      similarProjects: [],
      dialog: this.$route.query.PAQ === 'true',
      exposeAll: false,
      referralDialog: false,
      preApplicationPassed: false
    }
  },

  async fetch() {
    const { data } = await this.$api.publicProjects.getSimilarJobs(this.$route.params.id)
    this.similarProjects = data.results
  },

  computed: {
    jobFieldSettings() {
      return this.$useLeConfig(this.projectData.legalEntityName, this.$ELeConfig.JOB_FIELD_SETTINGS)
    },

    isHiringDeadlineVisibel() {
      return this.isJobFieldVisible('hiringDeadline')
    },

    isPublishDateVisible() {
      return this.isJobFieldVisible('publishDate')
    },

    isFreelancerActive() {
      return this.$store.state.auth.user?.isActive
    },
    talentId() {
      return this.$store.state.auth.user?.id
    },

    canApplyForProject() {
      return this.projectData?.status === EProjectStatus.ACTIVE
    },

    generateReferralLinks() {
      if (this.$auth.loggedIn && this.isFreelancerActive && !this.canApplyForProject) {
        return {
          id: 'jobShare',
          text: this.$t('talent.global.project.public.re.share'),
          showButton: true
        }
      }
      return {
        showButton: false
      }
    },
    isExternalAppLink() {
      return this.$useConfig(this.$EClientConfig.ENABLE_EXTERNAL_APP_LINK)
    },
    isBoardMode() {
      return this.$useConfig(this.$EClientConfig.ENABLE_BOARD_MODE)
    },
    applyButtons() {
      const projectId = this.$route.params.id
      const appData = this.applicationData
      const isLoggedIn = this.$auth.loggedIn
      const externalAppLink = this.projectData?.externalApplicationLink

      return [
        {
          condition: () =>
            isLoggedIn &&
            appData?.canApplyAgain &&
            appData?.multipleApplicationsAllowed &&
            appData?.previousApplications?.length,
          button: {
            id: 'jobApply',
            url: this.$link.application(projectId),
            text: this.$t('talent.global.project.public.re.apply'),
            showButton: true
          }
        },
        {
          condition: () =>
            (isLoggedIn && appData?.canApply && this.isExternalAppLink) ||
            (!isLoggedIn && this.canApplyForProject && this.isExternalAppLink),
          button: {
            id: 'jobRedirect',
            url: externalAppLink,
            text: this.$t('talent.global.project.public.apply'),
            showButton: Boolean(externalAppLink),
            isExternalAppLink: true
          }
        },
        {
          condition: () => isLoggedIn && appData?.canApply && !this.isExternalAppLink,
          button: {
            id: 'jobApply',
            url: this.$link.application(projectId),
            text: this.$t('talent.global.project.public.apply'),
            showButton: true
          }
        },
        {
          condition: () =>
            (isLoggedIn && !appData?.canApply) ||
            (!isLoggedIn && !this.isExternalAppLink && this.isBoardMode),
          button: {
            showButton: false
          }
        },
        {
          condition: () =>
            (!isLoggedIn && this.canApplyForProject) || (!isLoggedIn && !this.canApplyForProject),
          button: {
            id:
              !isLoggedIn && this.canApplyForProject
                ? 'publicJobsRegsiterToApply'
                : 'publicJobsRegsiter',
            url: this.$link.signup(),
            text: this.$t(
              `talent.global.project.public.${
                !isLoggedIn && this.canApplyForProject ? 'register' : 'signup'
              }`
            ),
            showButton: true
          }
        }
      ]
    },

    generateLinks() {
      const matched = this.applyButtons.find((button) => button.condition())
      return matched?.button || { showButton: false }
    },

    makeEntityBool() {
      const entityName = this.projectData?.legalEntityName?.toLowerCase()
      const exists = this.$te(`talent.global.entity.${entityName}.disclaimer`)
      const verb = this.$t(`talent.global.entity.${entityName}.disclaimer`)

      return (exists && verb !== '') || false
    },

    entityDisclaimer() {
      const entityName = this.projectData?.legalEntityName?.toLowerCase()
      return this.$t(`talent.global.entity.${entityName}.disclaimer`)
    },

    makeTitleFontSmallerOnMobile() {
      const longestWord = this.findLongestWord(this.projectData.title)
      return longestWord > 14 && this.$vuetify.breakpoint.name === 'xs'
    },

    buttonXLarge() {
      return (
        this.$vuetify.breakpoint.sm ||
        this.$vuetify.breakpoint.md ||
        this.$vuetify.breakpoint.lg ||
        this.$vuetify.breakpoint.xl
      )
    },

    projectDetails() {
      return projectDetails(this, this.projectData)
    },

    logoPath() {
      return this.$legalEntityLogo(this.projectData.legalEntityName, {
        width: 64,
        height: 64,
        fit: 'cover'
      })
    }
  },

  mounted() {
    const jobHandleIdStorage = localStorage.getItem('preApplicationPassed')

    if (jobHandleIdStorage && jobHandleIdStorage === this.projectData.id.toString()) {
      this.preApplicationPassed = true
      return
    }

    const jobHandleQuery = this.$route.query.preapplicationpassed
    if (jobHandleQuery === 'yes') {
      localStorage.setItem('preApplicationPassed', this.projectData.id.toString())
      this.preApplicationPassed = true
    }
  },

  methods: {
    async prepareProject() {
      if (!this.$auth.loggedIn && this.canApplyForProject) {
        const cookieData = projectCookieData(this.projectData)
        await this.$auth.$storage.setUniversal('projectData', cookieData, true)
      }
    },

    async saveJobData(url) {
      if (
        !this.preApplicationPassed &&
        this.projectData.preApplicationQuestions.length &&
        !this.applicationData?.canResumeApply &&
        this.canApplyForProject &&
        this.$useConfig(this.$EClientConfig.ENABLE_PAQ)
      ) {
        this.dialog = true
      } else {
        await this.prepareProject()
        this.$router.push(url)
      }
    },

    checkPreApplicationQuestions(result) {
      switch (result) {
        case 'success':
          return this.handlePreAppQuestionsSuccess()
        case 'fail-proceed':
          return this.handlePreAppQuestionsFailed()
      }
    },

    handlePreAppQuestionsFailed() {
      return this.$router.push(this.$link.projectSearch())
    },

    async handlePreAppQuestionsSuccess() {
      if (this.canApplyForProject) {
        await this.prepareProject()

        const desiredRoute = this.$auth.loggedIn
          ? this.$link.application(this.projectData.id)
          : this.$link.signup()

        return await this.$router.push(desiredRoute)
      }
    },
    findLongestWord(str) {
      const longestWord = str.split(' ').reduce(function (longest, currentWord) {
        return currentWord.length > longest.length ? currentWord : longest
      }, '')
      return longestWord.length
    },
    handleOpenReferralModal() {
      this.referralDialog = true
    },

    isJobFieldVisible(field) {
      return get(this.jobFieldSettings?.talent, field, 'show') === 'show'
    }
  }
}
